"use client";

import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

import { cn } from "./ui/utils";

export interface AnimatedFilterIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AnimatedFilterIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const LINE_VARIANTS: Variants = {
  normal: (custom: number) => ({
    rotate: 0,
    y: 0,
    opacity: 1,
    x1: custom === 1 ? 3 : custom === 2 ? 7 : 10,
    x2: custom === 1 ? 21 : custom === 2 ? 17 : 14,
    y1: custom === 1 ? 6 : custom === 2 ? 12 : 18,
    y2: custom === 1 ? 6 : custom === 2 ? 12 : 18,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
    },
  }),
  animate: (custom: number) => ({
    rotate: custom === 1 ? 45 : custom === 3 ? -45 : 0,
    y: custom === 1 ? 6 : custom === 3 ? -6 : 0,
    opacity: custom === 2 ? 0 : 1,
    x1: 4,
    x2: 20,
    y1: custom === 1 ? 6 : custom === 2 ? 12 : 18,
    y2: custom === 1 ? 6 : custom === 2 ? 12 : 18,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
    },
  }),
};

const AnimatedFilterIcon = forwardRef<AnimatedFilterIconHandle, AnimatedFilterIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Top line - full width (3 to 21) */}
          <motion.line
            animate={controls}
            custom={1}
            initial="normal"
            variants={LINE_VARIANTS}
          />
          {/* Middle line - medium width (7 to 17) */}
          <motion.line
            animate={controls}
            custom={2}
            initial="normal"
            variants={LINE_VARIANTS}
          />
          {/* Bottom line - short width (10 to 14) */}
          <motion.line
            animate={controls}
            custom={3}
            initial="normal"
            variants={LINE_VARIANTS}
          />
        </svg>
      </div>
    );
  }
);

AnimatedFilterIcon.displayName = "AnimatedFilterIcon";

export { AnimatedFilterIcon };